import React from 'react';
import styled from 'styled-components';
import { ArrowRight, Shield, Users, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import medPhoto from '../assets/photo_med.png';
import { useSiteContent } from '../hooks/useSiteContent';

const HeroContainer = styled.section`
  margin-top: 90px;
  min-height: calc(100vh - 90px);
  background: linear-gradient(135deg, #001a45 0%, #002664 50%, #003d99 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  background-image: 
    radial-gradient(circle at 80% 20%, rgba(254, 202, 0, 0.12) 0%, transparent 40%),
    radial-gradient(circle at 20% 80%, rgba(200, 16, 46, 0.08) 0%, transparent 40%),
    url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`;

const FloatingShape = styled.div`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.02);
  animation: float 6s ease-in-out infinite;
  
  &:nth-child(1) {
    width: 400px;
    height: 400px;
    top: -10%;
    right: 10%;
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    width: 300px;
    height: 300px;
    bottom: 10%;
    left: 5%;
    animation-delay: 2s;
  }
  
  &:nth-child(3) {
    width: 200px;
    height: 200px;
    top: 40%;
    right: 30%;
    animation-delay: 4s;
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 2rem 6rem;
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  align-items: center;
  gap: 3rem;
  position: relative;
  z-index: 1;
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
    padding: 2rem 1.5rem;
  }
`;

// IMAGE A GAUCHE
const HeroImage = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  order: 1;
  animation: fadeIn 1s ease-out 0.2s backwards;

  &::before {
    content: '';
    position: absolute;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(254, 202, 0, 0.15) 0%, transparent 70%);
    border-radius: 50%;
    bottom: -50px;
    left: 50%;
    transform: translateX(-50%);
  }

  &::after {
    content: '';
    position: absolute;
    width: 350px;
    height: 350px;
    border: 2px solid rgba(255, 255, 255, 0.08);
    border-radius: 50%;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }

  img {
    position: relative;
    z-index: 1;
    max-height: 600px;
    width: auto;
    object-fit: contain;
    filter: drop-shadow(0 30px 60px rgba(0, 0, 0, 0.4));
    transform: scale(1.1);
  }

  @media (max-width: 1024px) {
    order: 2;
    
    img {
      max-height: 350px;
      transform: scale(1);
    }
    
    &::before {
      width: 350px;
      height: 350px;
    }
    
    &::after {
      width: 250px;
      height: 250px;
    }
  }
`;

// TEXTE A DROITE
const HeroText = styled.div`
  color: var(--white);
  order: 2;
  animation: fadeInUp 0.8s ease-out;

  @media (max-width: 1024px) {
    order: 1;
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  padding: 0.6rem 1.2rem;
  border-radius: var(--radius-full);
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--white);
  animation: fadeInUp 0.6s ease-out 0.3s backwards;

  svg {
    color: var(--accent-gold);
  }
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: clamp(2.2rem, 4vw, 3.5rem);
  font-weight: 800;
  line-height: 1.15;
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
  color: var(--white);
  animation: fadeInUp 0.6s ease-out 0.4s backwards;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.2);

  span {
    color: var(--accent-gold);
    display: block;
    margin-top: 0.25rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.8;
  margin-bottom: 2rem;
  max-width: 520px;
  animation: fadeInUp 0.6s ease-out 0.5s backwards;

  @media (max-width: 1024px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2.5rem;
  animation: fadeInUp 0.6s ease-out 0.6s backwards;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
  }
`;

const Button = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 1.75rem;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 1rem;
  transition: var(--transition);
  white-space: normal;
  text-align: center;
  line-height: 1.3;

  ${props =>
    props.$primary && `
    background: var(--accent-gold);
    color: var(--primary-blue-dark);
    box-shadow: 0 4px 20px rgba(254, 202, 0, 0.4);
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 30px rgba(254, 202, 0, 0.5);
    }
  `}

  ${props =>
    props.$secondary && `
    background: rgba(255, 255, 255, 0.1);
    color: var(--white);
    border: 2px solid rgba(255, 255, 255, 0.4);
    backdrop-filter: blur(10px);
    
    &:hover {
      background: var(--white);
      color: var(--primary-blue);
      border-color: var(--white);
    }
  `}

  @media (max-width: 640px) {
    width: 100%;
    max-width: 300px;
  }
`;

const Features = styled.div`
  display: flex;
  gap: 2.5rem;
  animation: fadeInUp 0.6s ease-out 0.7s backwards;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
  }
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  
  svg {
    color: var(--accent-gold);
    flex-shrink: 0;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: var(--white);
  opacity: 0.6;
  font-size: 0.75rem;
  animation: bounce 2s infinite;
  cursor: pointer;
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateX(-50%) translateY(0); }
    40% { transform: translateX(-50%) translateY(-10px); }
    60% { transform: translateX(-50%) translateY(-5px); }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Hero = () => {
  const { content } = useSiteContent('home_hero');
  const heroContent = content || {};

  return (
    (<HeroContainer id="accueil">
      <HeroBackground />
      <FloatingShape />
      <FloatingShape />
      <FloatingShape />
      <HeroContent>
        {/* IMAGE À GAUCHE */}
        <HeroImage>
          <img src={medPhoto} alt="Médiateur de la République" />
        </HeroImage>

        {/* TEXTE À DROITE */}
        <HeroText>
          <Badge>
            <Shield size={16} />
            {heroContent.badge || 'Autorité administrative indépendante'}
          </Badge>

          <HeroTitle>
            {heroContent.title || 'Votre voix compte.'}
            <span>{heroContent.highlight || 'La médiation pour tous.'}</span>
          </HeroTitle>

          <HeroSubtitle>
            {heroContent.subtitle || "Le médiateur est l'intercesseur entre l'Administration publique et les citoyens. Déposez votre réclamation en toute transparence et confidentialité."}
          </HeroSubtitle>

          <HeroButtons>
            <Button to={heroContent.primaryButtonLink || '/services/depot-plainte'} $primary>
              {heroContent.primaryButtonLabel || 'Déposer une réclamation'}
              <ArrowRight size={20} />
            </Button>
            <Button to={heroContent.secondaryButtonLink || '/services/suivi'} $secondary>
              {heroContent.secondaryButtonLabel || 'Suivre ma réclamation'}
            </Button>
          </HeroButtons>

          <Features>
            <Feature>
              <Scale size={22} />
              <span>{heroContent.featureOne || 'Gratuit et confidentiel'}</span>
            </Feature>
            <Feature>
              <Users size={22} />
              <span>{heroContent.featureTwo || "À l'écoute de tous"}</span>
            </Feature>
          </Features>
        </HeroText>
      </HeroContent>
      <ScrollIndicator>
        <span>Découvrir</span>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 5v14M5 12l7 7 7-7" />
        </svg>
      </ScrollIndicator>
    </HeroContainer>)
  );
};

export default Hero;

